import { useMemo } from 'react';
import { useNavigate, useParams } from '@tanstack/react-router';
import {
  useCalendarEvents,
  useProject,
  useProjects,
  useRescheduleCalendarEvent,
  useRescheduleTask,
  useTags,
  useTasks,
  useWorkspace,
  useWorkspaceMembers,
  useWorkspaceStatuses,
} from '@fluxter/shared';
import { ProjectPageHeader } from '@/components/ProjectPageHeader';
import { ProjectViewSwitcher } from '@/components/ProjectViewSwitcher';
import {
  BoardTopBarActions,
  NewTaskCtaButton,
  type BoardFilter,
} from '@/components/BoardTopBarActions';
import {
  CalendarEventDialog,
  type DialogMode,
} from '@/components/CalendarEventDialog';
import {
  CalendarView,
  type CalendarEventRow,
  type CalendarTaskRow,
  type CalendarViewMode,
} from '@/components/CalendarView';
import {
  computeEventsRangeForView,
  NewEventCtaButton,
} from '@/components/CalendarHelpers';
import { useSetTopBarSlots } from '@/components/TopBarSlots';
import { toast } from 'sonner';
import { useState } from 'react';
import { useCreateTask } from '@fluxter/shared';

interface TaskAttrs {
  title?: string;
  code?: string;
  status_id?: string;
  assignee_id?: string | null;
  due_at?: string | null;
}

interface TaskItem {
  id: string;
  attributes?: TaskAttrs;
}

interface StatusAttrs {
  name?: string;
  color?: string;
  category?: 'todo' | 'in_progress' | 'complete';
}

interface MemberAttrs {
  name?: string;
  email?: string;
}

interface TagAttrs {
  name?: string;
  color?: string;
}

interface CalendarEventAttrs {
  title?: string;
  start_at?: string;
  end_at?: string | null;
  is_all_day?: boolean;
  location?: string | null;
  project_id?: string | null;
  project_name?: string | null;
  creator_id?: string;
  attendees?: ReadonlyArray<{ user_id?: string; name?: string | null; email?: string | null }>;
}

interface ProjectListAttrs {
  name?: string;
  slug?: string;
}

interface ProjectListItem {
  id: string;
  attributes?: ProjectListAttrs;
}

interface CalendarEventItem {
  id: string;
  attributes?: CalendarEventAttrs;
}

/**
 * Glass redesign — Phase 6.
 *
 * Project-scoped calendar. Now a thin data adapter around
 * `<CalendarView>` — same component used by `WorkspaceHomePage`'s
 * calendar widget, just fed a different task source. The view itself
 * (Day/Week/Month, overdue lane, mini-month, hour grid) lives in the
 * reusable component.
 */
export const CalendarPage = () => {
  const { workspaceSlug, projectSlug } = useParams({
    from: '/w/$workspaceSlug/p/$projectSlug/calendar',
  });
  const navigate = useNavigate();

  const workspace = useWorkspace(workspaceSlug);
  const project = useProject(workspaceSlug, projectSlug);
  const projects = useProjects(workspaceSlug);
  const tasks = useTasks(workspaceSlug, projectSlug);
  const statuses = useWorkspaceStatuses(workspaceSlug);
  const members = useWorkspaceMembers(workspaceSlug);
  const tags = useTags(workspaceSlug);
  const createTask = useCreateTask(workspaceSlug, projectSlug);
  const rescheduleTask = useRescheduleTask(workspaceSlug);
  const rescheduleEvent = useRescheduleCalendarEvent(workspaceSlug);

  // Lifted from CalendarView in 13c so the page can derive the
  // events-fetch range from focal+view (narrow refetch on navigate)
  // and click-empty-cell create flows have access to the date.
  const [view, setView] = useState<CalendarViewMode>('week');
  const [focal, setFocal] = useState<Date>(() => new Date());

  const eventsRange = useMemo(
    () => computeEventsRangeForView(view, focal),
    [view, focal],
  );
  const eventsQuery = useCalendarEvents(workspaceSlug, projectSlug, eventsRange);

  // Dialog state. `mode` doubles as the open flag — null means closed.
  const [dialogMode, setDialogMode] = useState<DialogMode | null>(null);
  const [editingEventId, setEditingEventId] = useState<string | null>(null);

  const [filter, setFilter] = useState<BoardFilter>({ assigneeIds: [], tagIds: [] });

  const projectName = (project.data as { data?: { attributes?: { name?: string } } } | undefined)
    ?.data?.attributes?.name;
  const workspaceName = (workspace.data as { data?: { attributes?: { name?: string } } } | undefined)
    ?.data?.attributes?.name;

  const statusList =
    (statuses.data as { data?: Array<{ id: string; attributes?: StatusAttrs }> } | undefined)
      ?.data ?? [];
  const statusById = useMemo(
    () => new Map(statusList.map((s) => [s.id, s.attributes ?? {}])),
    [statusList],
  );

  const memberList =
    (members.data as { data?: Array<{ id: string; attributes?: MemberAttrs }> } | undefined)
      ?.data ?? [];
  const tagList =
    (tags.data as { data?: Array<{ id: string; attributes?: TagAttrs }> } | undefined)?.data ?? [];

  const taskList = (tasks.data as { data?: TaskItem[] } | undefined)?.data ?? [];
  const eventList =
    (eventsQuery.data as { data?: CalendarEventItem[] } | undefined)?.data ?? [];

  // Map API event → CalendarEventRow. Skip rows missing `start_at`
  // (defensive — should never happen for project-scoped events but
  // typing is permissive). The Meetings filter chip lives inside
  // CalendarView; per-person filtering applies to events too via
  // `filteredEvents` in the view.
  const calendarEvents: CalendarEventRow[] = useMemo(
    () =>
      eventList
        .filter((e) => Boolean(e.attributes?.start_at))
        .map((e) => {
          const a = e.attributes ?? {};
          return {
            id: e.id,
            title: a.title ?? 'Untitled',
            startAt: a.start_at ?? '',
            endAt: a.end_at ?? null,
            isAllDay: Boolean(a.is_all_day),
            location: a.location ?? null,
            projectName: a.project_name ?? null,
            attendees: (a.attendees ?? []).map((p) => ({
              userId: p.user_id ?? '',
              name: p.name ?? null,
              email: p.email ?? null,
            })),
          };
        }),
    [eventList],
  );

  // Map API task → CalendarTaskRow. Only tasks with a due_at are
  // surfaced (calendar can't render undated rows). Topbar filter
  // applies the same assignee/tag predicate as Board/List.
  const calendarTasks: CalendarTaskRow[] = useMemo(() => {
    const wanted = new Set(filter.tagIds);
    return taskList
      .filter((t) => Boolean(t.attributes?.due_at))
      .filter((t) => {
        if (filter.assigneeIds.length > 0) {
          return (
            t.attributes?.assignee_id !== null &&
            t.attributes?.assignee_id !== undefined &&
            filter.assigneeIds.includes(t.attributes.assignee_id)
          );
        }
        return true;
      })
      .filter((t) => {
        if (filter.tagIds.length > 0) {
          const tagIds = (t as { attributes?: { tag_ids?: string[] } }).attributes?.tag_ids ?? [];
          return tagIds.some((id) => wanted.has(id));
        }
        return true;
      })
      .map((t) => {
        const a = t.attributes ?? {};
        const status = a.status_id ? statusById.get(a.status_id) : undefined;
        const assignee = a.assignee_id
          ? memberList.find((m) => m.id === a.assignee_id)
          : undefined;
        return {
          id: t.id,
          code: a.code ?? '',
          title: a.title ?? 'Untitled',
          statusColor: status?.color ?? '#94a3b8',
          isComplete: status?.category === 'complete',
          assigneeId: a.assignee_id ?? null,
          assigneeName: assignee?.attributes?.name ?? null,
          assigneeEmail: assignee?.attributes?.email ?? null,
          dueAt: a.due_at ?? '',
        };
      });
  }, [taskList, filter, statusById, memberList]);

  const memberOptions = useMemo(
    () =>
      memberList.map((m) => ({
        id: m.id,
        name: m.attributes?.name ?? null,
        email: m.attributes?.email ?? null,
      })),
    [memberList],
  );

  const tagOptions = useMemo(
    () =>
      tagList.map((t) => ({
        id: t.id,
        name: t.attributes?.name ?? 'tag',
        color: t.attributes?.color ?? '#64748b',
      })),
    [tagList],
  );

  // Find the project's ULID for default-prefilling the create dialog.
  const projectListData =
    (projects.data as { data?: ProjectListItem[] } | undefined)?.data ?? [];
  const projectIdFromSlug = useMemo(
    () => projectListData.find((p) => p.attributes?.slug === projectSlug)?.id ?? null,
    [projectListData, projectSlug],
  );

  // Build the dialog-ready project list (id+name+slug) for the Project
  // picker dropdown inside the dialog.
  const dialogProjects = useMemo(
    () =>
      projectListData
        .filter((p) => Boolean(p.attributes?.slug && p.attributes?.name))
        .map((p) => ({
          id: p.id,
          slug: p.attributes?.slug ?? '',
          name: p.attributes?.name ?? '',
        })),
    [projectListData],
  );

  // Look up the editing event in raw API form so we have creator_id +
  // project_id + every attendee in the shape the dialog expects.
  const editingEvent = useMemo(() => {
    if (!editingEventId) return undefined;
    const row = eventList.find((e) => e.id === editingEventId);
    if (!row) return undefined;
    const a = row.attributes ?? {};
    return {
      id: row.id,
      title: a.title ?? 'Untitled',
      startAt: a.start_at ?? '',
      endAt: a.end_at ?? null,
      isAllDay: Boolean(a.is_all_day),
      location: a.location ?? null,
      projectName: a.project_name ?? null,
      attendees: (a.attendees ?? []).map((p) => ({
        userId: p.user_id ?? '',
        name: p.name ?? null,
        email: p.email ?? null,
      })),
      creatorId: a.creator_id ?? '',
      projectId: a.project_id ?? null,
    };
  }, [editingEventId, eventList]);

  const handleOpenTask = (taskUlid: string): void => {
    void navigate({
      to: '/w/$workspaceSlug/t/$taskUlid',
      params: { workspaceSlug, taskUlid },
    });
  };

  const handleOpenEvent = (eventUlid: string): void => {
    setEditingEventId(eventUlid);
    setDialogMode('edit');
  };

  const handleNewEvent = (): void => {
    setEditingEventId(null);
    setDialogMode('create');
  };

  const closeDialog = (): void => {
    setDialogMode(null);
    setEditingEventId(null);
  };

  // Drag-to-reschedule handler for tasks. CalendarView passes the
  // destination date as a `YYYY-MM-DD` string in the user's local
  // calendar; we forward straight through. `useRescheduleTask` does
  // optimistic patching, so the chip snaps to its new bucket
  // immediately and settles when the server responds.
  const handleRescheduleTask = (taskUlid: string, dueAtDate: string): void => {
    rescheduleTask.mutate(
      { taskUlid, due_at: dueAtDate },
      {
        onError: (err) =>
          toast.error(err instanceof Error ? err.message : 'Could not reschedule task.'),
      },
    );
  };

  // Drag-to-reschedule for events. CalendarView already computed the
  // shifted start_at + end_at preserving time-of-day for timed events;
  // we just pipe to the mutation.
  const handleRescheduleEvent = (input: {
    eventUlid: string;
    startAt: string;
    endAt: string | null;
  }): void => {
    rescheduleEvent.mutate(
      { eventUlid: input.eventUlid, start_at: input.startAt, end_at: input.endAt },
      {
        onError: (err) =>
          toast.error(err instanceof Error ? err.message : 'Could not reschedule event.'),
      },
    );
  };

  const handleNewTask = (): void => {
    createTask.mutate(
      { title: 'New task' },
      {
        onSuccess: (resp) => {
          const id =
            (resp as { data?: { id?: string } } | undefined)?.data?.id ?? null;
          if (id) handleOpenTask(id);
        },
        onError: (err) =>
          toast.error(err instanceof Error ? err.message : 'Could not create task.'),
      },
    );
  };

  const topBarRight = useMemo(
    () => (
      <>
        <BoardTopBarActions
          filter={filter}
          setFilter={setFilter}
          members={memberOptions}
          tags={tagOptions}
        />
        <ProjectViewSwitcher
          workspaceSlug={workspaceSlug}
          projectSlug={projectSlug}
          current="calendar"
        />
      </>
    ),
    [filter, memberOptions, tagOptions, workspaceSlug, projectSlug],
  );
  const topBarCta = useMemo(
    () => (
      <div className="flex items-center gap-2">
        <NewEventCtaButton onClick={handleNewEvent} />
        <NewTaskCtaButton onClick={handleNewTask} />
      </div>
    ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );
  useSetTopBarSlots({ right: topBarRight, cta: topBarCta });

  return (
    <>
      <ProjectPageHeader
        workspaceSlug={workspaceSlug}
        workspaceName={workspaceName}
        projectSlug={projectSlug}
        projectName={projectName}
        current="calendar"
      />
      {/*
        Negative -mx-6 cancels the workspace shell's px-6 so the
        calendar paints edge-to-edge — `<CalendarView>` owns its own
        22px padding via the `cal-shell` rule.
      */}
      <div className="-mx-6 -my-6 flex h-[calc(100%+3rem)] flex-1 flex-col">
        <CalendarView
          tasks={calendarTasks}
          events={calendarEvents}
          members={memberOptions}
          view={view}
          setView={setView}
          focal={focal}
          setFocal={setFocal}
          onOpenTask={handleOpenTask}
          onOpenEvent={handleOpenEvent}
          onRescheduleTask={handleRescheduleTask}
          onRescheduleEvent={handleRescheduleEvent}
        />
      </div>

      {dialogMode ? (
        <CalendarEventDialog
          open
          onClose={closeDialog}
          workspaceSlug={workspaceSlug}
          mode={dialogMode}
          event={dialogMode === 'edit' ? editingEvent : undefined}
          members={memberOptions}
          projects={dialogProjects}
          defaultProjectId={
            dialogMode === 'create' ? projectIdFromSlug ?? undefined : undefined
          }
        />
      ) : null}
    </>
  );
};
